'use strict';
// Importación de una instalación (hoja 19): JSON sustemia.energia v1/v2 o CSV del modelo de relevamiento.
//
// Las reglas son las del prototipo (validarEstructura, importarModeloCsv, combinarConExistente, normalizarInstalacion).
// Lo que agrega producción (hoja 23, preguntas 56 y 60):
//  - el prototipo se detiene en el primer error; acá se devuelven TODOS, por sección y fila. Para eso cada parte
//    (la ficha y cada fila) se valida sola con el mismo validador del prototipo, y al final el archivo completo
//    (para lo que cruza filas: meses duplicados, período base contra la serie…);
//  - nombres repetidos dentro de una misma lista se rechazan;
//  - si hay errores no se devuelve instalación (no se guarda nada); sin errores, vista previa de lo que cambia.
const P = require('./prototipo');
const { clonar, fijarContexto, inicializarDesdeArchivo } = require('./comun');

// Listas de la instalación (forma interna) con su nombre en el JSON, en el CSV y para el usuario.
const LISTAS = [
  { k: '_monthly', json: 'consumos_mensuales', csv: '#MENSUAL', etiqueta: 'Datos mensuales' },
  { k: 'fuentes', json: 'fuentes', csv: '#FUENTES', etiqueta: 'Fuentes' },
  { k: 'seus', json: 'seus', csv: '#USE', etiqueta: 'USE' },
  { k: 'machinery', json: 'machinery', csv: '#MAQUINARIA', etiqueta: 'Maquinaria' },
  { k: 'operations', json: 'operations', csv: '#OPERACIONES', etiqueta: 'Operaciones' },
  { k: 'ecms', json: 'ecms', csv: '#MEDIDAS', etiqueta: 'Medidas' },
  { k: 'docs', json: 'docs', csv: '#DOCUMENTOS', etiqueta: 'Documentos' },
  { k: 'objetivos', json: 'objetivos', etiqueta: 'Objetivos' },
  { k: 'factoresEstaticos', json: 'factoresEstaticos', etiqueta: 'Factores estáticos' },
  { k: 'combustibles', json: 'combustibles', etiqueta: 'Combustibles' },
  { k: 'conversiones', json: 'conversiones', etiqueta: 'Conversiones' },
  { k: 'idens', json: 'idens', etiqueta: 'IDEn' },
  { k: 'lbHist', json: 'lbHist', etiqueta: 'Historial de la LBEn' },
];
const LISTA_DE = Object.fromEntries(LISTAS.map((l) => [l.k, l]));

// Clave de fila de cada lista (la misma que usa combinarConExistente para conservar campos).
const CLAVES = {
  _monthly: (x) => x.month,
  seus: (x) => x.name,
  machinery: (x) => (x.linea || '') + '|' + x.nombre,
  operations: (x) => x.nombre,
  ecms: (x) => x.name,
  docs: (x) => x.tipo + '|' + x.nombre,
};
const NOMBRE_FILA = { _monthly: 'mes', seus: 'USE', machinery: 'equipo', operations: 'operación', ecms: 'medida', docs: 'documento' };

const intentar = (fn) => { try { fn(); return null; } catch (e) { return e.message; } };

// ---------- Validación por partes (forma interna, antes de normalizar) ----------

// Copia mínima de una operación: solo lo que necesitan las medidas para deducir su tipo de energía.
const operacionDeReferencia = (o) => ({
  nombre: o && o.nombre,
  cat: 'Productivo',
  fuente: typeof (o && o.fuente) === 'string' ? o.fuente : '',
  tipoFuente: ['elec', 'term', 'mixta'].includes(o && o.tipoFuente) ? o.tipoFuente : '',
});

function validarPorPartes(f) {
  const errores = [];
  const vacias = Object.fromEntries(LISTAS.map((l) => [l.k, []]));
  // 1) Ficha y bloques sueltos (tarifas, requisitos de detalle, formulario…), sin filas ni período base.
  const ficha = { ...clonar(f), ...vacias };
  delete ficha.lb;
  const eF = intentar(() => P.normalizarInstalacion(ficha));
  if (eF) errores.push({ lista: null, indice: null, mensaje: eF });
  // 2) Cada fila sola, sobre una ficha mínima válida (así un error de la ficha no se repite en cada fila).
  const base = { id: 'FILA', name: 'fila', unidadProdCorta: 'u', _ejemplo: f._ejemplo === true, ...vacias };
  for (const { k } of LISTAS) {
    (Array.isArray(f[k]) ? f[k] : []).forEach((fila, i) => {
      const b = clonar(base);
      b[k] = [clonar(fila)];
      if (k === 'ecms' || k === 'idens') b.operations = (f.operations || []).map(operacionDeReferencia);
      const e = intentar(() => P.normalizarInstalacion(b));
      if (e) errores.push({ lista: k, indice: i, mensaje: e });
    });
  }
  // 3) Nombres repetidos dentro de una lista (hoja 23, pregunta 56: sin ids, las filas se resuelven por nombre).
  for (const k of ['seus', 'machinery', 'operations', 'ecms']) {
    const vistos = new Map();
    (f[k] || []).forEach((x, i) => {
      const c = CLAVES[k](x || {});
      if (vistos.has(c)) {
        errores.push({ lista: k, indice: i, mensaje: `Nombre repetido: hay otro ${NOMBRE_FILA[k]} «${c.replace(/^\|/, '').replace('|', ' · ')}» (fila ${vistos.get(c) + 1}). Cada fila necesita un nombre distinto.` });
      } else vistos.set(c, i);
    });
  }
  // 4) Lo que cruza filas (meses duplicados, período base contra la serie, historial…): el archivo completo.
  if (!errores.length) {
    const e = intentar(() => P.normalizarInstalacion(clonar(f)));
    if (e) errores.push({ lista: null, indice: null, mensaje: e });
  }
  return errores;
}

// ---------- CSV ----------

const separadorCsv = (lineas) => {
  const l1 = lineas.find((l) => /[;,]/.test(l)) || '';
  return l1.split(';').length > l1.split(',').length ? ';' : ',';
};
// Mismo filtro que _importarModeloCsv(): qué filas de cada sección se leen.
const FILA_LEIDA = { '#MAQUINARIA': (r) => r[1], '#DOCUMENTOS': (r) => r[0] && r[1] };

// Divide el CSV en secciones con su línea en el archivo (1 = primera línea).
function seccionesCsv(texto) {
  const lineas = String(texto).replace(/^﻿/, '').replace(/\r/g, '').split('\n');
  const sep = separadorCsv(lineas);
  const secciones = [];
  let cur = null;
  lineas.forEach((l, i) => {
    const primera = (P.parseCsvLinea(l, sep)[0] || '');
    if (primera.startsWith('#')) { cur = { nombre: primera.trim(), marca: l, encabezado: null, filas: [] }; secciones.push(cur); return; }
    if (!cur || l.trim() === '') return;
    if (cur.encabezado == null) { cur.encabezado = l; return; }
    const r = P.parseCsvLinea(l, sep);
    if ((FILA_LEIDA[cur.nombre] || ((x) => x[0]))(r)) cur.filas.push({ texto: l, linea: i + 1 });
  });
  return { sep, secciones, lineas };
}

// Devuelve los errores de lectura y, si la ficha se pudo leer, la instalación SIN las filas que fallaron
// (así la validación posterior encuentra también los errores de las demás filas).
function leerCsv(texto) {
  const { sep, secciones, lineas: todas } = seccionesCsv(texto);
  const sec = (n) => secciones.find((s) => s.nombre === n);
  const errores = [];
  const ficha = sec('#FICHA');
  if (!ficha || !sec('#MENSUAL')) {
    return { errores: [{ seccion: 'Archivo', fila: null, linea: null, mensaje: 'El CSV no tiene las secciones #FICHA y #MENSUAL del modelo' }] };
  }
  const lineasDe = (s, filas) => [s.marca, s.encabezado || '', ...filas.map((x) => x.texto)];
  // Ficha sola
  const eF = intentar(() => P.importarModeloCsv([...lineasDe(ficha, ficha.filas), '#MENSUAL'].join('\n')));
  if (eF) errores.push({ seccion: '#FICHA', fila: null, linea: null, mensaje: eF });
  // Cada fila de cada sección, sobre una ficha mínima válida
  const fichaMin = [`#FICHA${sep}${sep}`, `campo${sep}valor`, `id${sep}FILA`];
  for (const s of secciones) {
    if (s.nombre === '#FICHA' || !LISTAS.some((l) => l.csv === s.nombre)) continue;
    s.filas.forEach((fila, i) => {
      const otras = s.nombre === '#MENSUAL' ? [] : ['#MENSUAL'];
      const e = intentar(() => P.importarModeloCsv([...fichaMin, ...otras, ...lineasDe(s, [fila])].join('\n')));
      if (e) errores.push({ seccion: s.nombre, fila: i + 1, linea: fila.linea, mensaje: e });
    });
  }
  if (eF) return { errores };
  const fallidas = new Set(errores.map((x) => x.linea));
  const legible = todas.filter((_, i) => !fallidas.has(i + 1)).join('\n');
  let f;
  const e = intentar(() => { f = P.importarModeloCsv(legible); });
  if (e) return { errores: [...errores, { seccion: 'Archivo', fila: null, linea: null, mensaje: e }] };
  f._monthly.forEach((m) => { delete m.src; }); // marca interna del prototipo («viene del modelo»)
  // Para ubicar en el archivo los errores de la validación posterior: línea de cada fila leída.
  const lineas = {};
  for (const l of LISTAS) {
    if (l.csv && sec(l.csv)) lineas[l.k] = sec(l.csv).filas.filter((x) => !fallidas.has(x.linea)).map((x) => x.linea);
  }
  const filaDeLinea = new Map();
  secciones.forEach((s) => s.filas.forEach((x, i) => filaDeLinea.set(x.linea, i + 1)));
  return { errores, f, lineas, filaDeLinea };
}

// ---------- Vista previa ----------

const CAMPOS_FICHA = ['name', 'country', 'sector', 'arquetipo', 'unidadProd', 'unidadProdCorta', 'area', 'employees', 'shifts',
  'alcance', 'tarifas', 'factores', 'var2Nombre', 'var2Unidad', 'fuentes', 'combustibles', 'conversiones', 'objetivos', 'factoresEstaticos', 'idens', 'metricParams'];
const DERIVADOS_FILA = ['id', '_casc', '_nota', '_aviso', 'createdAt', 'updatedAt'];

// Para decidir si una fila cambió: vacío, null, false y [] equivalen a «no está» (p. ej. el CSV trae evento '' y
// excluir false donde lo guardado no tiene la clave).
function comparable(k, x) {
  const c = clonar(x || {});
  DERIVADOS_FILA.forEach((d) => delete c[d]);
  Object.keys(c).forEach((d) => { const v = c[d]; if (v === '' || v === null || v === false || (Array.isArray(v) && !v.length)) delete c[d]; });
  if (k === 'ecms') {
    if (c.baseOp) ['kwh', 'usd', 'payback'].forEach((d) => delete c[d]); // con operación base se recalculan
    // valores por defecto de la hoja 19: tipo vacío = eficiencia (o suministro si el nombre lo indica), verificación vacía = estimado
    c.tipo = c.tipo || (P.esSuministro(c) ? 'suministro' : 'eficiencia');
    c.verificacion = c.verificacion || 'estimado';
    if (c.kwhRenovable === 0) delete c.kwhRenovable;
  }
  return JSON.stringify(c, Object.keys(c).sort());
}

function vistaPrevia(prev, f) {
  const listas = {};
  for (const k of Object.keys(CLAVES)) {
    const antes = new Map((prev ? prev[k] || [] : []).map((x) => [CLAVES[k](x), x]));
    const ahora = new Map((f[k] || []).map((x) => [CLAVES[k](x), x]));
    const r = { agregadas: [], modificadas: [], eliminadas: [], sinCambios: 0 };
    ahora.forEach((x, c) => {
      if (!antes.has(c)) r.agregadas.push(c);
      else if (comparable(k, antes.get(c)) !== comparable(k, x)) r.modificadas.push(c);
      else r.sinCambios++;
    });
    antes.forEach((x, c) => { if (!ahora.has(c)) r.eliminadas.push(c); });
    listas[LISTA_DE[k].etiqueta] = r;
  }
  // ficha: mismo criterio de vacío; la nota de tarifas/factores es un texto informativo (el CSV la reescribe)
  const valorFicha = (v) => {
    if (v === '' || v === undefined) return 'null';
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = Object.fromEntries(Object.entries(v).filter(([k, x]) => k !== 'nota' && x !== '' && x != null));
      return Object.keys(o).length ? JSON.stringify(o, Object.keys(o).sort()) : 'null';
    }
    return Array.isArray(v) && !v.length ? 'null' : JSON.stringify(v);
  };
  const ficha = prev ? CAMPOS_FICHA.filter((c) => valorFicha(prev[c]) !== valorFicha(f[c])) : [];
  return { modo: prev ? 'actualizar' : 'crear', id: f.id, nombre: f.name, fichaModificada: ficha, listas };
}

// ---------- Entrada principal ----------

/**
 * Valida un archivo a importar y, si no tiene errores, devuelve la instalación que quedaría guardada.
 * @param {{formato:'json'|'csv', contenido: object|string}} entrada  JSON (objeto o texto) o texto CSV
 * @param {{existente?: object, fechaReferencia?: string}} opciones  existente: archivo v2 de la instalación ya guardada
 *        con el mismo id (reimportación: se combinan según la hoja 19 «Mismo id»)
 * @returns {{errores: Array<{seccion, fila, linea, mensaje}>, instalacion?, archivo?, vistaPrevia?}}
 *          Con errores no hay instalación: no se guarda nada.
 */
function prepararImportacion(entrada, opciones = {}) {
  fijarContexto(opciones);
  const { formato, contenido } = entrada || {};
  const err = (mensaje, seccion = 'Archivo') => ({ errores: [{ seccion, fila: null, linea: null, mensaje }] });
  let f;
  let lineas = null;
  let filaDeLinea = null;
  let erroresLectura = [];
  if (formato === 'json') {
    let obj = contenido;
    if (typeof obj === 'string') {
      try { obj = JSON.parse(obj.replace(/^﻿/, '')); } catch (e) { return err('El archivo no es un JSON válido: ' + e.message); }
    }
    const e = intentar(() => { f = inicializarDesdeArchivo(obj); });
    if (e) return err(e);
  } else if (formato === 'csv') {
    const r = leerCsv(contenido);
    if (!r.f) return { errores: r.errores };
    ({ f, lineas, filaDeLinea } = r);
    erroresLectura = r.errores;
    if (!erroresLectura.length && !f._monthly.length) return err('La instalación no trae consumos mensuales (evidencias de facturas y producción)', '#MENSUAL');
  } else {
    return err('Formato no soportado: usá JSON, CSV o el .xlsx del modelo');
  }

  // Un ejemplo del módulo no se puede pisar ni duplicar (hoja 19 «Mismo id»).
  if (P.esEjemploBase(String(f.id).trim())) {
    return err(`El código «${String(f.id).trim()}» es de una instalación de ejemplo (solo lectura): usá otro código.`, 'Ficha');
  }
  let prev = null;
  if (opciones.existente) {
    const eP = intentar(() => { prev = P.normalizarInstalacion(P.san(inicializarDesdeArchivo(opciones.existente))); });
    if (eP) return err('La instalación guardada tiene datos que no pasan la validación, así que no se puede combinar con el archivo: ' + eP, 'Instalación guardada');
    if (String(prev.id) !== String(f.id).trim()) {
      return err(`El archivo es de la instalación «${String(f.id).trim()}» y se está importando sobre «${prev.id}».`, 'Ficha');
    }
  }
  const eC = intentar(() => { f = P.san(P.combinarConExistente(f, prev && clonar(prev))); });
  if (eC) return err(eC, 'Línea de base');

  const errores = validarPorPartes(f).map(({ lista, indice, mensaje }) => {
    const l = lista && LISTA_DE[lista];
    if (!l) return { seccion: formato === 'csv' ? '#FICHA' : 'Ficha', fila: null, linea: null, mensaje };
    const linea = lineas && lineas[lista] ? lineas[lista][indice] ?? null : null;
    // en el CSV la fila se cuenta en el archivo (la lista leída no tiene las filas que fallaron al leer)
    const fila = linea != null && filaDeLinea ? filaDeLinea.get(linea) : indice + 1;
    return { seccion: formato === 'csv' && l.csv ? l.csv : l.etiqueta, fila, linea, mensaje };
  });
  if (errores.length || erroresLectura.length) {
    return { errores: [...erroresLectura, ...errores].sort((a, b) => (a.linea ?? 0) - (b.linea ?? 0)) };
  }

  const instalacion = P.normalizarInstalacion(f);
  P.limpiarReferenciasExternas(instalacion); // archivos viejos con bench*: se descartan sin error (hoja 01, fila 12)
  P.asegurarColecciones(instalacion); // ids de gráficos que no existen: se descartan
  return { errores: [], instalacion, archivo: P.exportSustemia(instalacion), vistaPrevia: vistaPrevia(prev, instalacion) };
}

// Valida una instalación editada en pantalla (archivo v2) con las mismas reglas que la importación (hoja 30),
// sin exigir consumos. Devuelve la lista de errores [{ seccion, fila, linea, mensaje }] (vacía si está bien).
function validarInstalacion(archivo, opciones = {}) {
  fijarContexto(opciones);
  let f;
  const e = intentar(() => { f = P.san(inicializarDesdeArchivo(archivo, { permitirSinMeses: true })); });
  if (e) return [{ seccion: 'Ficha', fila: null, linea: null, mensaje: e }];
  return validarPorPartes(f).map(({ lista, indice, mensaje }) => {
    const l = lista && LISTA_DE[lista];
    return l ? { seccion: l.etiqueta, fila: indice + 1, linea: null, mensaje } : { seccion: 'Ficha', fila: null, linea: null, mensaje };
  });
}

// Código de instalación que trae el archivo, sin validarlo (para saber sobre qué instalación se importa).
function idDeEntrada({ formato, contenido } = {}) {
  try {
    if (formato === 'json') {
      const obj = typeof contenido === 'string' ? JSON.parse(contenido.replace(/^﻿/, '')) : contenido;
      const id = obj && obj.instalacion && obj.instalacion.id;
      return id != null && String(id).trim() ? String(id).trim() : null;
    }
    if (formato === 'csv') {
      const { sep, secciones } = seccionesCsv(contenido);
      const ficha = secciones.find((s) => s.nombre === '#FICHA');
      const fila = ficha && ficha.filas.map((x) => P.parseCsvLinea(x.texto, sep)).find((r) => String(r[0]).trim() === 'id');
      return fila && String(fila[1] || '').trim() ? String(fila[1]).trim() : null;
    }
  } catch (e) { /* archivo ilegible: lo informa prepararImportacion */ }
  return null;
}

module.exports = { prepararImportacion, vistaPrevia, idDeEntrada, validarInstalacion };
